import { useParsedStorage } from '../../../../core/hooks/useParsedStorage'
import type { AgentSessionSourceType } from '../../../components/agent/AgentSessionQueries'
import { useAgentSessionsQuery } from '../../../components/agent/AgentSessionQueries'
import { parseAsString, useQueryState } from 'nuqs'
import { z } from 'zod'

const AgentSessionTabConfigSchema = z.object({
  conversationIds: z.array(z.string()),
})
const AgentSessionConversationIdSchema = z.uuid()
const AGENT_SESSION_TAB_VALIDATION_INTERVAL_MS = 5_000

export const NewAgentSessionConversationId = '__new_agent_session__'
export const LatestAgentChatConversationId = '__latest_agent_chat__'

export const DEFAULT_AGENT_SESSION_TABS_QUERY_PARAM = 'conversation'

function isLatestAgentChatConversationId(conversationId: string): boolean {
  return conversationId === LatestAgentChatConversationId
}

// Exposed so links elsewhere in the app (e.g. the encounter timeline) can use
// the same `conversation` param via the central encounter route serializer.
export const EncounterAgentSessionQueryMap = {
  conversation: parseAsString,
}

interface UseAgentSessionTabsParams {
  readonly encounterId: string
  readonly optimisticConversationIds: ReadonlyArray<string>
}

export function useAgentSessionTabs({
  encounterId,
  optimisticConversationIds,
}: UseAgentSessionTabsParams) {
  return useBrowserStorageAgentSessionTabs({
    newConversationId: NewAgentSessionConversationId,
    optimisticConversationIds,
    storageKey: `encounter-agent-session-tabs:${encounterId}`,
    validationSourceId: encounterId,
    validationSourceType: 'encounter',
  })
}

interface UseAgentSessionTabsStateParams {
  readonly activeConversationId: string
  readonly defaultConversationId?: string
  readonly newConversationId?: string
  readonly onActiveConversationChange: (conversationId: string) => void
  readonly optimisticConversationIds: ReadonlyArray<string>
  readonly storageKey: string
  readonly validationSourceId?: string
  readonly validationSourceType?: AgentSessionSourceType
}

/**
 * Storage-backed agent conversation tabs with injectable active selection.
 * Surfaces that own a URL param should prefer `useBrowserStorageAgentSessionTabs`.
 */
export function useAgentSessionTabsState({
  activeConversationId,
  defaultConversationId,
  newConversationId = NewAgentSessionConversationId,
  onActiveConversationChange,
  optimisticConversationIds,
  storageKey,
  validationSourceId,
  validationSourceType,
}: UseAgentSessionTabsStateParams) {
  const defaultActiveConversationId = defaultConversationId ?? newConversationId
  const [agentSessionTabConfig, setAgentSessionTabConfig] = useParsedStorage(
    storageKey,
    {
      initialValue: {
        conversationIds: [defaultActiveConversationId],
      },
      schema: AgentSessionTabConfigSchema,
      strategy: 'local',
    },
  )

  const parsedStoredConversationIds =
    agentSessionTabConfig.conversationIds.length > 0
      ? agentSessionTabConfig.conversationIds
      : [newConversationId]
  const conversationIdsToValidate = [
    ...parsedStoredConversationIds,
    activeConversationId,
  ]
  const persistedConversationIds = conversationIdsToValidate.filter(
    (conversationId, index) => {
      if (conversationId === newConversationId) {
        return false
      }

      const parsedConversationId =
        AgentSessionConversationIdSchema.safeParse(conversationId)
      if (parsedConversationId.success === false) {
        return false
      }

      return conversationIdsToValidate.indexOf(conversationId) === index
    },
  )
  const validationConversationIds =
    persistedConversationIds.length > 0 ? persistedConversationIds : undefined
  const validationQuery = useAgentSessionsQuery({
    conversation_ids: validationConversationIds,
    enabled: validationConversationIds !== undefined,
    refetchInterval: AGENT_SESSION_TAB_VALIDATION_INTERVAL_MS,
    source_id: validationSourceId,
    source_type: validationSourceType,
    staleTime: 0,
  })
  const availableConversationIds =
    validationConversationIds === undefined
      ? []
      : validationQuery.isSuccess
        ? validationQuery.data.map((conversation) => {
            return conversation.id
          })
        : undefined
  const resolvedTabs = resolveAgentSessionTabState({
    activeConversationId,
    availableConversationIds,
    newConversationId,
    optimisticConversationIds,
    storedConversationIds: parsedStoredConversationIds,
  })
  const validatedStoredConversationIds = resolvedTabs.storedConversationIds
  const validatedActiveTabConversationId = resolvedTabs.activeConversationId
  const tabConversationIds = resolvedTabs.tabConversationIds

  const openConversationTab = (conversationId: string) => {
    const isConversationAlreadyOpen =
      validatedStoredConversationIds.includes(conversationId)

    if (isConversationAlreadyOpen === false) {
      const conversationIds =
        conversationId === newConversationId
          ? [...validatedStoredConversationIds, conversationId]
          : [
              ...validatedStoredConversationIds.filter((tab) => {
                return tab !== newConversationId
              }),
              conversationId,
            ]

      setAgentSessionTabConfig({
        conversationIds,
      })
    }

    onActiveConversationChange(conversationId)
  }

  const closeConversationTab = (conversationId: string) => {
    const closedConversationIndex = tabConversationIds.indexOf(conversationId)
    const remainingConversationIds = tabConversationIds.filter(
      (_conversationId) => {
        return _conversationId !== conversationId
      },
    )

    const conversationIds =
      remainingConversationIds.length === 0
        ? [newConversationId]
        : remainingConversationIds

    const nextActiveConversationId =
      remainingConversationIds.at(closedConversationIndex) ??
      remainingConversationIds.at(closedConversationIndex - 1) ??
      newConversationId
    if (
      validatedActiveTabConversationId === conversationId &&
      nextActiveConversationId !== undefined
    ) {
      onActiveConversationChange(nextActiveConversationId)
    }

    setAgentSessionTabConfig({
      conversationIds,
    })
  }

  const closeOtherConversationTabs = (conversationId: string) => {
    setAgentSessionTabConfig({
      conversationIds: [conversationId],
    })
    onActiveConversationChange(conversationId)
  }

  const closeConversationTabsToTheRight = (conversationId: string) => {
    const currentIndex = tabConversationIds.indexOf(conversationId)
    if (currentIndex === -1) {
      return
    }

    const remainingConversationIds = tabConversationIds.slice(
      0,
      currentIndex + 1,
    )

    setAgentSessionTabConfig({
      conversationIds: remainingConversationIds,
    })

    if (
      remainingConversationIds.includes(validatedActiveTabConversationId) ===
      false
    ) {
      onActiveConversationChange(conversationId)
    }
  }

  const replaceConversationTab = ({
    fromConversationId,
    toConversationId,
  }: {
    readonly fromConversationId: string
    readonly toConversationId: string
  }) => {
    const conversationIds = replaceConversationId({
      conversationIds: validatedStoredConversationIds,
      fromConversationId,
      toConversationId,
    })

    setAgentSessionTabConfig({
      conversationIds,
    })

    if (validatedActiveTabConversationId === fromConversationId) {
      onActiveConversationChange(toConversationId)
    }
  }

  const reconcileStoredTabsRef = (node: HTMLElement | null) => {
    if (node === null) {
      return
    }

    const isStoredStateCurrent =
      agentSessionTabConfig.conversationIds.length ===
        validatedStoredConversationIds.length &&
      agentSessionTabConfig.conversationIds.every((conversationId, index) => {
        return conversationId === validatedStoredConversationIds.at(index)
      })
    if (isStoredStateCurrent === false) {
      setAgentSessionTabConfig({
        conversationIds: validatedStoredConversationIds,
      })
    }

    if (activeConversationId !== validatedActiveTabConversationId) {
      onActiveConversationChange(validatedActiveTabConversationId)
    }
  }

  return {
    activeTabConversationId: validatedActiveTabConversationId,
    closeConversationTab,
    closeConversationTabsToTheRight,
    closeOtherConversationTabs,
    openConversationTab,
    reconcileStoredTabsRef,
    replaceConversationTab,
    setActiveTabConversationId: onActiveConversationChange,
    tabConversationIds,
  }
}

interface UseBrowserStorageAgentSessionTabsParams {
  readonly defaultConversationId?: string
  readonly newConversationId?: string
  readonly optimisticConversationIds: ReadonlyArray<string>
  /** URL search param that stores the active tab conversation id. */
  readonly queryParam?: string
  readonly storageKey: string
  readonly validationSourceId?: string
  readonly validationSourceType?: AgentSessionSourceType
}

export function useBrowserStorageAgentSessionTabs({
  defaultConversationId,
  newConversationId = NewAgentSessionConversationId,
  optimisticConversationIds,
  queryParam = DEFAULT_AGENT_SESSION_TABS_QUERY_PARAM,
  storageKey,
  validationSourceId,
  validationSourceType,
}: UseBrowserStorageAgentSessionTabsParams) {
  const defaultActiveConversationId = defaultConversationId ?? newConversationId
  const [activeTabConversationId, setActiveTabConversationId] = useQueryState(
    queryParam,
    parseAsString.withDefault(defaultActiveConversationId),
  )
  const resolvedActiveConversationId = isLatestAgentChatConversationId(
    activeTabConversationId,
  )
    ? defaultActiveConversationId
    : activeTabConversationId

  return useAgentSessionTabsState({
    activeConversationId: resolvedActiveConversationId,
    defaultConversationId,
    newConversationId,
    onActiveConversationChange: (conversationId) => {
      void setActiveTabConversationId(conversationId)
    },
    optimisticConversationIds,
    storageKey,
    validationSourceId,
    validationSourceType,
  })
}

export function replaceConversationId({
  conversationIds,
  fromConversationId,
  toConversationId,
}: {
  readonly conversationIds: ReadonlyArray<string>
  readonly fromConversationId: string
  readonly toConversationId: string
}) {
  const nextConversationIds = conversationIds.map((conversationId) => {
    return conversationId === fromConversationId
      ? toConversationId
      : conversationId
  })

  const hasTargetConversation = nextConversationIds.includes(toConversationId)
  const conversationIdsWithTarget = hasTargetConversation
    ? nextConversationIds
    : [...nextConversationIds, toConversationId]

  return conversationIdsWithTarget.filter((conversationId, index) => {
    return conversationIdsWithTarget.indexOf(conversationId) === index
  })
}

export function isConversationValidForAgentSession({
  availableConversationIds,
  conversationId,
  newConversationId,
  optimisticConversationIds,
}: {
  readonly availableConversationIds?: ReadonlyArray<string>
  readonly conversationId: string
  readonly newConversationId: string
  readonly optimisticConversationIds: ReadonlyArray<string>
}) {
  if (conversationId === newConversationId) {
    return true
  }

  if (optimisticConversationIds.includes(conversationId)) {
    return true
  }

  const parsedConversationId =
    AgentSessionConversationIdSchema.safeParse(conversationId)
  if (parsedConversationId.success === false) {
    return false
  }

  return availableConversationIds
    ? availableConversationIds.includes(conversationId)
    : true
}

export function resolveAgentSessionTabState({
  activeConversationId,
  availableConversationIds,
  newConversationId,
  optimisticConversationIds,
  storedConversationIds,
}: {
  readonly activeConversationId: string
  readonly availableConversationIds?: ReadonlyArray<string>
  readonly newConversationId: string
  readonly optimisticConversationIds: ReadonlyArray<string>
  readonly storedConversationIds: ReadonlyArray<string>
}) {
  const validStoredConversationIds = storedConversationIds.filter(
    (conversationId) => {
      return isConversationValidForAgentSession({
        availableConversationIds,
        conversationId,
        newConversationId,
        optimisticConversationIds,
      })
    },
  )
  const validatedStoredConversationIds =
    validStoredConversationIds.length > 0
      ? validStoredConversationIds
      : [newConversationId]

  const isActiveConversationValid = isConversationValidForAgentSession({
    availableConversationIds,
    conversationId: activeConversationId,
    newConversationId,
    optimisticConversationIds,
  })
  const validatedActiveConversationId = isActiveConversationValid
    ? activeConversationId
    : (validatedStoredConversationIds.at(0) ?? newConversationId)

  const tabConversationIds = validatedStoredConversationIds.includes(
    validatedActiveConversationId,
  )
    ? validatedStoredConversationIds
    : [...validatedStoredConversationIds, validatedActiveConversationId]

  return {
    activeConversationId: validatedActiveConversationId,
    storedConversationIds: validatedStoredConversationIds,
    tabConversationIds,
  }
}
